// Coordinates the mobile/hardware back button across the guest webview's nested
// screens (tab stack, Stay room detail, bottom sheets…). Each pushed "step"
// registers a browser history entry plus the callback that undoes it; the
// single popstate listener below walks them off in LIFO order instead of
// letting the browser navigate the webview away entirely.
type BackHandler = () => void;

const handlers: BackHandler[] = [];
let attached = false;

function ensureListener() {
    if (attached || typeof window === 'undefined') return;
    attached = true;
    window.addEventListener('popstate', () => {
        const handler = handlers.pop();
        handler?.();
    });
}

// Call when a nested screen/sheet opens. `onBack` runs when the user backs out
// of it (via hardware/browser back, or closeBackEntry()).
export function pushBackEntry(onBack: BackHandler) {
    ensureListener();
    window.history.pushState({ guestBack: true }, '');
    handlers.push(onBack);
}

// Swaps the undo behaviour for the current depth without adding a new history
// entry — for screens that replace one nested view with another (e.g. Stay
// detail → booking form) rather than stacking on top of it.
export function replaceBackEntry(onBack: BackHandler) {
    if (handlers.length > 0) {
        handlers[handlers.length - 1] = onBack;
    } else {
        pushBackEntry(onBack);
    }
}

// Call from an in-app "close/back" control so it behaves exactly like the
// hardware back button (keeps browser history in sync).
export function closeBackEntry() {
    if (handlers.length > 0) window.history.back();
}

// Drops any pending entries without invoking their handlers (the caller has
// already applied the resulting UI state directly, e.g. switching tabs) and
// unwinds the matching browser history depth so a later back press doesn't
// land on stale entries.
export function resetBackEntries() {
    const n = handlers.length;
    if (n === 0) return;
    handlers.length = 0;
    window.history.go(-n);
}
